const SELECTOR = '[data-amelya-cart-bundle-progress]';
const GIFT_KEYS = ['_Amelya promotional gift', '_Gift linked to bundle', '_Gift discount required'];

function money(cents) {
  const currency = window.Shopify?.currency?.active || 'EUR';

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format((Number(cents) || 0) / 100);
  } catch (_) {
    return `${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
  }
}

function getBundleVariantId(root, size) {
  return Number(
    root.dataset[`bundle${size}VariantId`] ||
    root.getAttribute(`data-bundle-${size}-variant-id`) ||
    root.getAttribute(`data-bundle${size}-variant-id`) ||
    0
  );
}

function getBundlePrice(root, size) {
  const value = Number(
    root.dataset[`bundle${size}PriceCents`] ||
    root.getAttribute(`data-bundle-${size}-price-cents`) ||
    root.getAttribute(`data-bundle${size}-price-cents`) ||
    0
  );

  if (value > 0) return value;

  const currency = window.Shopify?.currency?.active || 'EUR';

  const fallbackPrices = {
    EUR: { 2: 6990, 3: 7990, 4: 8990 },
    USD: { 2: 7900, 3: 8900, 4: 9900 },
    CAD: { 2: 7900, 3: 8900, 4: 9900 },
    GBP: { 2: 5900, 3: 6900, 4: 7900 },
    AUD: { 2: 11900, 3: 13400, 4: 14900 },
    AED: { 2: 29900, 3: 34900, 4: 39900 },
    SAR: { 2: 29900, 3: 34900, 4: 39900 },
    QAR: { 2: 29900, 3: 34900, 4: 39900 },
  };

  return fallbackPrices[currency]?.[size] || fallbackPrices.EUR[size] || 0;
}

function isGiftLine(item) {
  const props = item.properties || {};

  const hasGiftProperty = GIFT_KEYS.some((key) => Boolean(props[key]));
  if (hasGiftProperty) return true;

  const finalPrice = Number(item.final_price ?? item.price ?? 0);
  const finalLinePrice = Number(item.final_line_price ?? item.line_price ?? 0);
  const originalLinePrice = Number(item.original_line_price ?? item.line_price ?? 0);

  const discountSources = [
    ...(item.discounts || []),
    ...(item.discount_allocations || []),
    ...(item.line_level_discount_allocations || []),
  ];

  const hasGiftDiscount = discountSources.some((discount) => {
    const title = String(
      discount.title ||
      discount.discount_application?.title ||
      discount.discount_application?.value ||
      ''
    ).toLowerCase();

    return (
      title.includes('gift') ||
      title.includes('free') ||
      title.includes('reduction event promo') ||
      title.includes('promo')
    );
  });

  const isFreeDiscountedItem =
    finalPrice === 0 ||
    finalLinePrice === 0 ||
    (originalLinePrice > 0 && finalLinePrice === 0);

  return hasGiftDiscount || isFreeDiscountedItem;
}

function bundleSizeFromLine(item, root) {
  const props = item.properties || {};

  if (props.Bundle) {
    const match = String(props.Bundle).match(/(2|3|4)/);
    if (match) return Number(match[1]);
  }

  const variantId = Number(item.variant_id);
  if (variantId === getBundleVariantId(root, 2)) return 2;
  if (variantId === getBundleVariantId(root, 3)) return 3;
  if (variantId === getBundleVariantId(root, 4)) return 4;

  return 0;
}

function isBundleLine(item, root) {
  return bundleSizeFromLine(item, root) > 0;
}

function categoryFromItem(item) {
  return item.product_type || item.category || '';
}

function eligibleUnits(cart, root) {
  const units = [];

  (cart.items || []).forEach((item) => {
    if (isGiftLine(item)) return;
    if (isBundleLine(item, root)) return;

    const qty = parseInt(item.quantity || 0, 10);

    for (let i = 0; i < qty; i += 1) {
      units.push({
        item,
        key: item.key,
      });
    }
  });

  return units.slice(0, 4);
}

function activeBundleSize(cart, root) {
  let size = 0;

  for (const item of cart.items || []) {
    size = Math.max(size, bundleSizeFromLine(item, root));
  }

  return size;
}

function activeGiftLines(cart) {
  return (cart.items || []).filter(isGiftLine);
}

function selectedTotal(units) {
  return units.reduce((sum, unit) => {
    return sum + Number(unit.item.final_price || unit.item.price || 0);
  }, 0);
}

function setText(root, name, value) {
  const node = root.querySelector(`[data-${name}]`);
  if (node) node.textContent = value;
}

function toggle(root, name, show) {
  const node = root.querySelector(`[data-${name}]`);
  if (node) node.hidden = !show;
}

function i18n(root, key, fallback = '') {
  return root.dataset[`i18n${key.charAt(0).toUpperCase()}${key.slice(1)}`] || fallback;
}

function statusMessage(root, count, bundleReadyWithGift) {
  if (bundleReadyWithGift) {
    return i18n(root, 'statusComplete');
  }

  switch (count) {
    case 0:
      return i18n(root, 'statusZero');
    case 1:
      return i18n(root, 'statusOne');
    case 2:
      return i18n(root, 'statusTwo');
    case 3:
      return i18n(root, 'statusThree');
    case 4:
      return i18n(root, 'statusFour');
    default:
      return '';
  }
}

function buildProperties(size, units) {
  const properties = {
    Bundle: `${size} pieces`,
  };

  units.slice(0, size).forEach(({ item }, index) => {
    const n = index + 1;

    properties[`Piece ${n}`] = [
      categoryFromItem(item),
      item.product_title || item.title || '',
    ].filter(Boolean).join(' · ');

    properties[`_Piece ${n} variant`] =
      item.variant_title && item.variant_title !== 'Default Title'
        ? item.variant_title
        : '';

    properties[`_Piece ${n} variant ID`] = String(item.variant_id || '');
    properties[`_Piece ${n} SKU`] = item.sku || '';
    properties[`_Piece ${n} image`] = item.image || '';
    properties[`_Piece ${n} category`] = categoryFromItem(item);
  });

  return properties;
}

async function fetchCart() {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Could not read cart.');
  }

  return response.json();
}

async function postCart(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || data?.status) {
    throw new Error(
      data?.description || data?.message || 'Cart update failed.'
    );
  }

  return data;
}

async function removeLockedGiftIfNeeded(root, cart) {
  if (root.dataset.autoRemoveLockedGift !== 'true') return false;

  const existingBundleSize = activeBundleSize(cart, root);
  const individualCount = eligibleUnits(cart, root).length;
  const count = Math.max(existingBundleSize, individualCount);

  if (count >= 4) return false;

  const gifts = activeGiftLines(cart);
  if (!gifts.length) return false;

  for (const gift of gifts) {
    await postCart(`${window.Shopify.routes.root}cart/change.js`, {
      id: gift.key,
      quantity: 0,
    });
  }

  return true;
}

async function render(root) {
  const cart = await fetchCart();

  const giftRemoved = await removeLockedGiftIfNeeded(root, cart);
  if (giftRemoved) {
    return render(root);
  }

  const existingBundleSize = activeBundleSize(cart, root);
  const units = eligibleUnits(cart, root);

  const count = existingBundleSize || Math.min(units.length, 4);
  const applied = existingBundleSize >= 2;

  const cartItemCount = (cart.items || []).reduce((sum, item) => {
    return sum + Number(item.quantity || 0);
  }, 0);

  const hasDetectedGift = activeGiftLines(cart).length > 0;
  const hasFiveCartUnits = cartItemCount >= 5;
  const bundleReadyWithGift = count === 4 && (hasDetectedGift || hasFiveCartUnits);

  const offerSize = !applied && units.length >= 2
    ? Math.min(units.length, 4)
    : 0;

  const realTotal = offerSize ? selectedTotal(units.slice(0, offerSize)) : 0;
  const bundlePrice = offerSize ? getBundlePrice(root, offerSize) : 0;
  const saving = Math.max(realTotal - bundlePrice, 0);

  root.dataset.selectedCount = String(count);
  root.dataset.bundleApplied = applied ? 'true' : 'false';
  root.dataset.bundleReadyWithGift = bundleReadyWithGift ? 'true' : 'false';

  setText(
    root,
    'bundle-count',
    `${count} ${count === 1 ? i18n(root, 'pieceSelected') : i18n(root, 'piecesSelected')}`
  );

  setText(root, 'bundle-message', statusMessage(root, count, bundleReadyWithGift));
  setText(root, 'bundle-real-total', money(realTotal));
  setText(root, 'bundle-price', money(bundlePrice));
  setText(root, 'bundle-saving', `${i18n(root, 'youSave')} ${money(saving)}`);

  toggle(root, 'bundle-pricing', Boolean(offerSize) && !applied);
  toggle(root, 'bundle-apply-wrapper', Boolean(offerSize) && !applied);
  toggle(root, 'bundle-locked', count < 4);

  const giftBlock = root.querySelector('[data-bundle-gift]');
  if (giftBlock) {
    const showGiftBlock = count === 4 && !bundleReadyWithGift;
    giftBlock.hidden = !showGiftBlock;
    giftBlock.style.display = showGiftBlock ? '' : 'none';
  }

  const applyButton = root.querySelector('[data-apply-bundle-offer]');

  if (applyButton) {
    applyButton.disabled = !offerSize || applied;
    applyButton.dataset.bundleSize = String(offerSize || '');

    if (!applyButton.dataset.defaultText) {
      applyButton.dataset.defaultText = i18n(root, 'applyBundle');
    }

    if (!applied && offerSize) {
      applyButton.textContent = applyButton.dataset.defaultText;
    }
  }

  const dots = root.querySelectorAll('[data-bundle-dot]');
  dots.forEach((dot, index) => {
    dot.classList.toggle('is-active', index < count);
  });
}

async function applyOffer(root) {
  const cart = await fetchCart();
  const units = eligibleUnits(cart, root);
  const size = Math.min(units.length, 4);
  const bundleVariantId = getBundleVariantId(root, size);

  if (size < 2 || !bundleVariantId) {
    throw new Error('Bundle variant not found.');
  }

  const button = root.querySelector('[data-apply-bundle-offer]');
  if (button) {
    button.disabled = true;
    button.textContent = i18n(root, 'applyingBundle');
  }

  const selectedUnits = units.slice(0, size);
  const properties = buildProperties(size, selectedUnits);

  const usedByKey = {};

  selectedUnits.forEach((unit) => {
    usedByKey[unit.key] = (usedByKey[unit.key] || 0) + 1;
  });

  for (const [key, quantityToRemove] of Object.entries(usedByKey)) {
    const originalItem = cart.items.find((item) => item.key === key);
    if (!originalItem) continue;

    const newQuantity = Math.max(Number(originalItem.quantity || 0) - quantityToRemove, 0);

    await postCart(`${window.Shopify.routes.root}cart/change.js`, {
      id: key,
      quantity: newQuantity,
    });
  }

  await postCart(`${window.Shopify.routes.root}cart/add.js`, {
    items: [
      {
        id: Number(bundleVariantId),
        quantity: 1,
        properties,
      },
    ],
  });

  if (button) {
  button.textContent = `✓ ${i18n(root, 'bundleApplied')}`;
}

setTimeout(function() {
  window.location.href = `${window.Shopify.routes.root}checkout`;
}, 700);
}

const renderingRoots = new WeakSet();
let initAllTimer = 0;

function init(root) {
  if (!root || renderingRoots.has(root)) return;

  renderingRoots.add(root);

  render(root)
    .catch((error) => {
      console.error('[AMELYA BUNDLE PROGRESS RENDER ERROR]', error);
    })
    .finally(() => {
      renderingRoots.delete(root);
    });
}

function initAll() {
  document.querySelectorAll(SELECTOR).forEach(init);
}

function scheduleInitAll(delay = 80) {
  window.clearTimeout(initAllTimer);
  initAllTimer = window.setTimeout(() => {
    window.requestAnimationFrame(initAll);
  }, delay);
}

document.addEventListener('click', async function(event) {
  const button = event.target.closest('[data-apply-bundle-offer]');
  if (!button) return;

  const root = button.closest(SELECTOR);
  if (!root) return;

  event.preventDefault();
  event.stopPropagation();

  button.disabled = true;
  button.textContent = i18n(root, 'applyingBundle');

  try {
    await applyOffer(root);
  } catch (error) {
    console.error('[AMELYA APPLY BUNDLE ERROR]', error);
    button.disabled = false;
    button.textContent = i18n(root, 'tryAgain');
    setText(
      root,
      'bundle-message',
      i18n(root, 'activationError')
    );
  }
}, true);

document.addEventListener('DOMContentLoaded', () => scheduleInitAll(120));
document.addEventListener('cart:update', () => scheduleInitAll(120));
document.addEventListener('cart:refresh', () => scheduleInitAll(120));
document.addEventListener('shopify:section:load', () => scheduleInitAll(120));

function initAmelyaFirstOrderBenefit() {
  if (window.__amelyaFirstOrderBenefitReady) return;
  window.__amelyaFirstOrderBenefitReady = true;

  document.addEventListener('submit', function(event) {
    const form = event.target.closest('.cart-drawer-first-order__form');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();

    const wrapper = form.closest('.cart-drawer-first-order');
    if (!wrapper) return;

    const code = wrapper.querySelector('.cart-drawer-first-order__code');
    const button = form.querySelector('.cart-drawer-first-order__button');

    if (!code || !button) return;

    button.textContent = i18n(wrapper, 'pleaseWait', button.textContent);
    button.disabled = true;

    const formData = new FormData(form);

    fetch('/contact', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'text/html' },
    }).finally(function() {
      form.style.display = 'none';
      code.removeAttribute('hidden');
      button.disabled = false;
    });
  }, true);

  document.addEventListener('click', function(event) {
    const button = event.target.closest('[data-apply-cart-code]');
    if (!button) return;

    const defaultButtonText = button.textContent;

    event.preventDefault();
    event.stopPropagation();

    const discountInput = document.querySelector('#cart-discount');
    const discountForm = document.querySelector('.cart-discount__form');
    const discountSubmit = discountForm
      ? discountForm.querySelector('button[type="submit"]')
      : null;

    if (!discountInput || !discountForm) {
      const wrapper = button.closest('.cart-drawer-first-order');
      button.textContent = wrapper
        ? i18n(wrapper, 'copyUnavailable', defaultButtonText)
        : defaultButtonText;

      window.setTimeout(function() {
        button.textContent = wrapper
          ? i18n(wrapper, 'applyCode', defaultButtonText)
          : defaultButtonText;
      }, 2000);

      return;
    }

    discountInput.value = 'AMELYA20';
    discountInput.dispatchEvent(new Event('input', { bubbles: true }));
    discountInput.dispatchEvent(new Event('change', { bubbles: true }));

    const wrapper = button.closest('.cart-drawer-first-order');
    button.textContent = wrapper
      ? i18n(wrapper, 'applying', defaultButtonText)
      : defaultButtonText;
    button.disabled = true;

    if (discountSubmit) {
      discountSubmit.click();
    } else {
      discountForm.dispatchEvent(
        new Event('submit', { bubbles: true, cancelable: true })
      );
    }

    window.setTimeout(function() {
      button.textContent = wrapper
        ? i18n(wrapper, 'applied', defaultButtonText)
        : defaultButtonText;
    }, 900);
  }, true);
}


function initAmelyaDrawerScrollHint() {
  if (window.innerWidth > 749) return;

  const content =
    document.querySelector('.cart-drawer__content') ||
    document.querySelector('.cart-drawer__items') ||
    document.querySelector('cart-drawer .drawer__content') ||
    document.querySelector('.cart-drawer');

  if (!content) return;

  window.setTimeout(function() {
    const isScrollable = content.scrollHeight > content.clientHeight + 24;
    if (!isScrollable) return;

    content.classList.remove('is-scrollable-hint');

    window.requestAnimationFrame(function() {
      content.classList.add('is-scrollable-hint');
    });

    content.addEventListener(
      'scroll',
      function() {
        content.classList.remove('is-scrollable-hint');
      },
      { once: true }
    );
  }, 450);
}


initAmelyaFirstOrderBenefit();

document.addEventListener('DOMContentLoaded', initAmelyaDrawerScrollHint);
document.addEventListener('cart:update', initAmelyaDrawerScrollHint);
document.addEventListener('cart:refresh', initAmelyaDrawerScrollHint);
document.addEventListener('shopify:section:load', initAmelyaDrawerScrollHint);

document.addEventListener('click', function(event) {
  if (
    event.target.closest('[aria-controls*="cart"]') ||
    event.target.closest('[data-cart-drawer-toggle]') ||
    event.target.closest('.cart-count-bubble') ||
    event.target.closest('a[href="/cart"]')
  ) {
    window.setTimeout(initAmelyaDrawerScrollHint, 500);
  }
}, true);
